import type { BlobLike } from "@pdfking/compatibility"
import { ImportImage } from "@pdfking/compatibility"
import { BlobArray, BlobToUint8 } from "@pdfking/compatibility"
import {
  type PdfiumApi,
  PdfiumExports as E,
  type PdfiumTypeMap,
  type Pointer,
} from "@pdfking/pdfium-wrap"
import type {
  PDFConvertFunction,
  PDFSplitResult,
  Pixel,
  SpatialRectangle,
} from "@pdfking/renderer-api"
import type { Bytes } from "@pdfking/tfunc"
import { Items, IterableToArray, SafePromise } from "@pdfking/tfunc"

import { SaveDocument } from "../functions/SaveDocument"
import { ProvisionPdfium } from "../utils/ProvisionPdfium"

type PointerDoc = PdfiumTypeMap["FPDF_DOCUMENT"]
type PointerImageObj = PdfiumTypeMap["FPDF_PAGEOBJECT"]

type AttemptFunction = (
  api: PdfiumApi,
  blob: BlobLike,
  resolution: number
) => Promise<PDFSplitResult>

type ImageLoaderResult = {
  width: number
  height: number
  cleanup: () => Promise<void>
}

type ImageLoader = (
  api: PdfiumApi,
  doc: PointerDoc,
  imageObj: PointerImageObj,
  blob: BlobLike
) => Promise<ImageLoaderResult>

// Get image pixel size from image object using pdfium API
const getImagePixelSize = async (
  api: PdfiumApi,
  imageObj: PointerImageObj
): Promise<{ width: number; height: number }> => {
  const { safeCall, malloc, readBytes } = api

  const { pointer: widthPtr, free: freeWidth } = malloc(4 as Bytes)
  const { pointer: heightPtr, free: freeHeight } = malloc(4 as Bytes)

  await safeCall(
    E.FPDFImageObj_GetImagePixelSize,
    imageObj,
    widthPtr,
    heightPtr
  )

  const widthBytes = readBytes(widthPtr, 4 as Bytes)
  const heightBytes = readBytes(heightPtr, 4 as Bytes)

  freeWidth()
  freeHeight()

  return {
    width: new DataView(widthBytes.buffer).getUint32(0, true),
    height: new DataView(heightBytes.buffer).getUint32(0, true),
  }
}

// JPEG loader using FPDFImageObj_LoadJpegFileInline
const loadJpeg: ImageLoader = async (api, _doc, imageObj, blob) => {
  const { call, malloc, writeBytes, setInt32, writeTo, addCallback } = api

  const bytes = await BlobToUint8(blob)
  const { free: freeData } = writeBytes(bytes)
  const { pointer: fileAccessPtr, free: freeFileAccess } = malloc(12 as Bytes)

  const getBlock = (
    _param: number,
    position: number,
    pBuf: number,
    size: number
  ) => {
    writeTo(pBuf as Pointer, bytes.subarray(position, position + size))
    return 1
  }

  const { pointer: getBlockPtr, free: freeGetBlock } = addCallback(
    getBlock,
    "iiiii"
  )

  setInt32(fileAccessPtr, bytes.length)
  setInt32((fileAccessPtr + 4) as Pointer, getBlockPtr)
  setInt32((fileAccessPtr + 8) as Pointer, 0 as unknown as Pointer)

  const success = await call(
    E.FPDFImageObj_LoadJpegFileInline,
    0 as unknown as Pointer,
    0,
    imageObj,
    fileAccessPtr
  )

  if (!success) {
    freeData()
    freeFileAccess()
    freeGetBlock()
    throw new Error("Failed to load JPEG into image object")
  }

  const { width, height } = await getImagePixelSize(api, imageObj)

  return {
    width,
    height,
    cleanup: async () => {
      freeData()
      freeFileAccess()
      freeGetBlock()
    },
  }
}

// Convert RGBA to BGRA (swap R and B channels)
const rgbaToBgra = (data: Uint8Array): Uint8Array => {
  const result = new Uint8Array(data.length)
  for (let i = 0; i < data.length; i += 4) {
    result[i] = data[i + 2] // B <- R
    result[i + 1] = data[i + 1] // G
    result[i + 2] = data[i] // R <- B
    result[i + 3] = data[i + 3] // A
  }
  return result
}

// Bitmap loader using FPDFBitmap_CreateEx + FPDFImageObj_SetBitmap
const loadBitmap: ImageLoader = async (api, _doc, imageObj, blob) => {
  const { call, safeCall, writeBytes } = api

  const { width, height, data } = await ImportImage(blob)
  const bgraData = rgbaToBgra(data)

  const { pointer: dataPtr, free: freeData } = writeBytes(bgraData)

  const bitmap = await safeCall(
    E.FPDFBitmap_CreateEx,
    width,
    height,
    4, // FPDFBitmap_BGRA
    dataPtr,
    width * 4
  )

  await safeCall(
    E.FPDFImageObj_SetBitmap,
    0 as unknown as Pointer,
    0,
    imageObj,
    bitmap
  )

  return {
    width,
    height,
    cleanup: async () => {
      await call(E.FPDFBitmap_Destroy, bitmap)
      freeData()
    },
  }
}

// Unified image embedding with loader closure
async function embedImage(
  api: PdfiumApi,
  source: BlobLike,
  resolution: number,
  loader: ImageLoader
): Promise<PDFSplitResult> {
  const { call, safeCall } = api

  const doc = await safeCall(E.FPDF_CreateNewDocument)

  // Create page with temporary size, will resize after loading image
  const page = await safeCall(E.FPDFPage_New, doc, 0, 1, 1)
  const imageObj = await safeCall(E.FPDFPageObj_NewImageObj, doc)

  const {
    width: imgWidth,
    height: imgHeight,
    cleanup,
  } = await loader(api, doc, imageObj, source)

  const width = (imgWidth * 72) / resolution
  const height = (imgHeight * 72) / resolution

  // Resize page to actual dimensions
  await call(E.FPDFPage_SetMediaBox, page, 0, 0, width, height)

  await call(E.FPDFPageObj_Transform, imageObj, width, 0, 0, height, 0, 0)

  await call(E.FPDFPage_InsertObject, page, imageObj)
  await safeCall(E.FPDFPage_GenerateContent, page)
  await call(E.FPDF_ClosePage, page)

  const box = { x: 0, y: 0, width, height } as SpatialRectangle<Pixel>

  const blob = await IterableToArray(SaveDocument(api, doc)).then(buffers =>
    BlobArray(buffers, "application/pdf")
  )

  await cleanup()
  await call(E.FPDF_CloseDocument, doc)

  return {
    blob,
    trim_box: box,
    media_box: box,
  } as PDFSplitResult
}

const attempts: Record<string, AttemptFunction> = {
  "image/jpeg": (api, blob, res) => embedImage(api, blob, res, loadJpeg),
  "image/png": (api, blob, res) => embedImage(api, blob, res, loadBitmap),
}

function* iterateAttempts(contentType: string): Iterable<AttemptFunction> {
  if (attempts[contentType]) {
    yield attempts[contentType]
  }

  for (const [type, func] of Items(attempts)) {
    if (type !== contentType) {
      yield func
    }
  }
}

export const PDFConvert: PDFConvertFunction = async (input, resolution) => {
  const blob = await IterableToArray(input.stream).then(BlobArray)

  for await (const result of ProvisionPdfium(async function* (api) {
    for (const attemptFn of iterateAttempts(blob.type)) {
      const { value, error } = await SafePromise(
        attemptFn(api, blob, resolution)
      )

      if (error) {
        console.warn("PDFConvert attempt failed:", error)
      } else {
        yield value
        return
      }
    }

    throw new Error("No suitable conversion method found for image")
  })) {
    return result
  }
  throw new Error("No result from ProvisionPdfium")
}
